import React, { useRef, useState, useCallback, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { FormBuilder } from 'components/FormBuilder'
import dataDownloadFormJson from './dataDownloadForm.json'
import { generateDynamicYupSchema } from 'components/FormBuilder/generateYupSchemaFromJson'
import { useYupValidationResolver } from 'hooks/useYupValidationResolver'
import { CircularProgress, Button, Dialog, IconButton } from '@mui/material'
import { Turnstile } from '@marsidev/react-turnstile'
import { generateTokenService } from 'services/token'
import { requestUserDataExport } from 'services/settings'
import { MessageDialog } from 'components/modal/submodals/MessageDialog'
import { UserVerification } from 'components/UserVerification'
import closeLogo from 'images/icon-close.svg'

import 'components/PrivacyRightsRequestForm/PrivacyRightsRequestForm.scss'

// Token source reused from the privacy rights flow (recognized by the API and
// anonymous-allowed). The anonymous /token/validate-token path matches by
// email + token, so the source value is not significant for validation.
const TOKEN_SOURCE = 'PRIVACY_RIGHTS_REQUEST'

const SUCCESS_COPY =
  'If we have records that match your email, an email will be sent shortly with a link to your information. Thank you!'

/**
 * @param {object} props
 * @param {Array}  props.fields       Field schema. Defaults to the bundled JSON, so existing
 *                                    callers are unchanged. Celebrando passes a copy with
 *                                    translated labels; the field `name`s are what the API
 *                                    reads and never change.
 * @param {string} props.submitLabel  Submit button copy.
 * @param {object} props.success      `{ title, description }` for the confirmation modal.
 */
export default function DataDownloadForm({
  submitButtonClassName = 'request-form-button',
  formClassName = 'request-form',
  fields = dataDownloadFormJson.fields,
  submitLabel = 'Download My Data',
  success = { title: 'Action confirmed!', description: SUCCESS_COPY },
}) {
  const [registrationError, setRegistrationError] = useState()
  const [verificationDialogOpen, setVerificationDialogOpen] = useState(false)
  const [verificationData, setVerificationData] = useState(null)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false)
  const formDataRef = useRef(null)
  const turnstileRef = useRef(null)

  const jsonValidationYup = useMemo(
    () => generateDynamicYupSchema(fields),
    [fields],
  )

  const [isInProgress, setIsInProgress] = useState(false)
  const form = useForm({
    mode: 'onChange',
    defaultValues: {},
    resolver: useYupValidationResolver(jsonValidationYup),
  })
  const { handleSubmit, setValue, formState, register, reset } = form

  // Generate the verification code email for the data download flow
  const customGenerateToken = useCallback(async payload => {
    return generateTokenService({
      ...payload,
      tokenSource: TOKEN_SOURCE,
    })
  }, [])

  // Validate the code and trigger the export in a single call — the new endpoint
  // consumes the token itself, so this must be the only place we call it.
  const customValidateAndDownload = useCallback(async ({ token, email }) => {
    try {
      await requestUserDataExport({ email, token, tokenSource: TOKEN_SOURCE })
      return { success: true }
    } catch (error) {
      // Falsy return makes UserVerificationForm show "Invalid code"
      return null
    }
  }, [])

  const tokenServicePayload = useMemo(
    () => ({
      turnstileToken: verificationData?.turnstileToken,
    }),
    [verificationData?.turnstileToken],
  )

  const handleVerificationSuccess = useCallback(() => {
    setVerificationDialogOpen(false)
    setVerificationData(null)
    setIsInProgress(false)
    setSuccessDialogOpen(true)
    formDataRef.current = null
    reset()
  }, [reset])

  const handleDialogClose = () => {
    setVerificationDialogOpen(false)
    setVerificationData(null)
    setIsInProgress(false)
    turnstileRef.current?.reset()
  }

  const onSubmit = async data => {
    if (!turnstileRef.current?.getResponse()) {
      console.error('Turnstile token not available')
      setRegistrationError('Please complete the verification')
      return
    }

    setIsInProgress(true)
    setRegistrationError(null)

    formDataRef.current = data

    setVerificationData({
      email: data.email,
      firstName: data.firstName,
      lastName: data.lastName,
      turnstileToken: turnstileRef.current?.getResponse(),
    })

    setVerificationDialogOpen(true)
  }

  return (
    <>
      <FormBuilder
        form={form}
        onSubmit={handleSubmit(onSubmit)}
        register={register}
        fields={fields}
        setFieldValue={setValue}
        errors={registrationError}
        jsonFieldOnForm={'extraField'}
        className={formClassName}
        formHeader={null}
        fieldStyle={{}}
        disabledFields={[]}
      >
        {formState.errors?.server && (
          <div className="error">{formState.errors.server.message}</div>
        )}
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
          }}
        >
          <Turnstile
            ref={turnstileRef}
            siteKey={process.env.VISIBLE_TURNSTILE_SITE_KEY}
          />
          <Button
            disabled={isInProgress}
            className={submitButtonClassName}
            variant="contained"
            type="submit"
          >
            {isInProgress ? (
              <CircularProgress color="inherit" size={18} />
            ) : (
              submitLabel
            )}
          </Button>
        </div>
      </FormBuilder>
      <Dialog
        onClose={(_, reason) =>
          reason !== 'backdropClick' && handleDialogClose()
        }
        open={verificationDialogOpen}
      >
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            right: 8,
            top: 8,
            alignSelf: 'end',
          }}
        >
          <img src={closeLogo} alt="Close" />
        </IconButton>
        {verificationData && (
          <UserVerification
            hasTurnstile={false}
            onConfirm={handleVerificationSuccess}
            customGenerateTokenService={customGenerateToken}
            customValidateTokenService={customValidateAndDownload}
            tokenServicePayload={tokenServicePayload}
            email={verificationData.email}
            firstName={verificationData.firstName}
            lastName={verificationData.lastName}
          />
        )}
      </Dialog>
      <MessageDialog
        open={successDialogOpen}
        handleClose={() => setSuccessDialogOpen(false)}
        title={success.title}
        description={success.description}
      />
    </>
  )
}
